import { DeleteModal, Header, Screen, Text } from '@components'
import { useTheme } from '@react-navigation/native'
import { useGetAlbumById, usePaginateAlbumMemories, useDeleteAlbum } from '@hooks'
import { useLanguage } from '@context'
import React, { useCallback, useState } from 'react'
import { Image, StyleSheet, TouchableOpacity, View } from 'react-native'
import { ScrollView } from 'react-native-gesture-handler'
import { ScreenProfileProps } from 'src/navigation/navigator/ProfileNavigator'
import { useFocusEffect } from '@react-navigation/native'
import { useQueryClient } from '@tanstack/react-query'

const GAP = 2
const NUM_COLUMNS = 3

export const AlbumMemories: React.FC<ScreenProfileProps<'ProfileAlbumMemories'>> = ({
  route,
  navigation,
}): React.ReactElement => {
  const { albumId, readonly } = route.params
  const theme = useTheme()
  const { t } = useLanguage()
  const queryClient = useQueryClient()

  const { data: album, isLoading: isLoadingAlbum } = useGetAlbumById(albumId)
  const { flatData: memories, isFetching } = usePaginateAlbumMemories({ albumId })
  const { mutateAsync: deleteAlbum, isPending: isDeletingAlbum } = useDeleteAlbum()

  const [confirmVisible, setConfirmVisible] = useState(false)

  useFocusEffect(
    useCallback(() => {
      queryClient.invalidateQueries({ queryKey: ['album', albumId] })
      queryClient.invalidateQueries({ queryKey: ['album-memories'] })
    }, [albumId])
  )

  const goBack = () => navigation.canGoBack() ? navigation.goBack() : navigation.navigate('ProfileHome')

  const handleDelete = async () => {
    await deleteAlbum({ id: albumId })
    goBack()
  }

  const tilePercent = `${100 / NUM_COLUMNS}%` as const

  const firstMemory = memories[0]

  return (
    <Screen isPending={isLoadingAlbum || isFetching || isDeletingAlbum}>
      <Header
        iconLeft="arrowBack"
        iconColor={theme.colors.secondary}
        onPressLeft={goBack}
        title={album?.title ?? ''}
        iconRight={readonly ? undefined : 'edit'}
        onPressRight={() => {
          if (firstMemory) {
            navigation.navigate('ProfileAddMemory', { memoryId: firstMemory.id })
          } else {
            navigation.navigate('ProfileAddMemory', { albumId })
          }
        }}
      />
      <ScrollView>
        <View style={styles.grid}>
          {memories.map((memory) => (
            <TouchableOpacity
              key={memory.id}
              style={{ width: tilePercent, padding: GAP / 2 }}
              onPress={() => navigation.navigate('ProfileShowMemory', { memoryId: memory.id })}
              activeOpacity={0.85}
            >
              <Image
                source={{ uri: memory.mediaUrl }}
                style={styles.image}
                resizeMode="cover"
              />
            </TouchableOpacity>
          ))}
        </View>
      </ScrollView>

      {!readonly && (
        <TouchableOpacity
          style={styles.deleteButton}
          onPress={() => setConfirmVisible(true)}
        >
          <Text type="bodySmall" color={theme.colors.error}>{t('common.delete')}</Text>
        </TouchableOpacity>
      )}

      <DeleteModal
        isVisible={confirmVisible}
        setIsVisible={setConfirmVisible}
        title=""
        description={t('memoriesForm.deleteAlbumDescription')}
        deleteLabel={t('memoriesForm.deleteAlbum')}
        onDelete={handleDelete}
      />
    </Screen>
  )
}

const styles = StyleSheet.create({
  grid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: GAP / 2,
  },
  image: {
    width: '100%',
    aspectRatio: 1,
  },
  deleteButton: {
    alignSelf: 'flex-end',
    marginVertical: 8,
    marginRight: 16,
    padding: 8,
  },
})
